import { useCallback, useEffect, useMemo, useState } from 'react'
import { SAVE_BUTTON_STATES } from '@urbanx/agx.ui-components2'
import { AgxToastState } from 'types/commonTypes'
import {
  AustralianAddress,
  PropertyAttributesModel,
  PropertyAttributesModelBuiltOnOrVacantLandType,
  useInvalidateUseStaffGetPropertyDetailsForCampaign,
  useInvalidateUseStaffGetPropertyInformationProgressForCampaign,
  useStaffGetPropertyDetailsForCampaign,
  useStaffUpdatePropertyDetailsForCampaign,
} from '@urbanx/agx.campaigns.client'
import { isDirtyEqual } from 'utils/compares'
import { PropertyType } from '@urbanx/agx-ui-components'

interface UsePropertyDetailsSectionStateArgs {
  campaignId?: string
  updateToastState: (toastState: AgxToastState) => void
}

interface PropertyDetailsSectionState {
  propertyAddress: AustralianAddress | undefined
  setPropertyAddress: (value: AustralianAddress | undefined) => void
  propertyAttributes: PropertyAttributesModel | undefined
  hidePropertyAttributes: string[]
  setPropertyAttributes: (value: PropertyAttributesModel | undefined) => void
  hasUnsavedChanges: boolean
  saveState: (typeof SAVE_BUTTON_STATES)[keyof typeof SAVE_BUTTON_STATES]
  isSaving: boolean
  isPending: boolean
  isError: boolean
  handleSaveChanges: () => Promise<void>
}

const usePropertyDetailsSectionState = ({
  campaignId,
  updateToastState,
}: UsePropertyDetailsSectionStateArgs): PropertyDetailsSectionState => {
  const [propertyAddress, setPropertyAddress] = useState<
    AustralianAddress | undefined
  >(undefined)
  const [propertyAttributes, setPropertyAttributes] = useState<
    PropertyAttributesModel | undefined
  >(undefined)
  const [isSaving, setIsSaving] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const [hasError, setHasError] = useState(false)

  const {
    data: propertyDetailsData,
    isPending,
    isError,
  } = useStaffGetPropertyDetailsForCampaign(campaignId ?? '', !!campaignId)

  const invalidateStaffGetPropertyDetails =
    useInvalidateUseStaffGetPropertyDetailsForCampaign()
  const invalidateStaffGetPropertyInformationProgress =
    useInvalidateUseStaffGetPropertyInformationProgressForCampaign()

  const { staffUpdatePropertyDetailsForCampaign } =
    useStaffUpdatePropertyDetailsForCampaign({
      onSuccess: () => undefined,
      onError: () => undefined,
    })

  const hidePropertyAttributes = useMemo(() => {
    const hiddenAttributes: string[] = []
    if (
      [PropertyType.Rural, PropertyType.ResidentialLand].includes(
        propertyAttributes?.propertyType as PropertyType
      )
    ) {
      hiddenAttributes.push('NumberOfBeds')
      hiddenAttributes.push('NumberOfBathrooms')
      hiddenAttributes.push('NumberOfCarSpaces')

      if (
        propertyAttributes?.propertyType === PropertyType.ResidentialLand ||
        propertyAttributes?.builtOnOrVacantLandType ===
          PropertyAttributesModelBuiltOnOrVacantLandType.VacantLand
      ) {
        hiddenAttributes.push('InternalArea')
      }
    }

    return hiddenAttributes
  }, [
    propertyAttributes?.propertyType,
    propertyAttributes?.builtOnOrVacantLandType,
  ])

  useEffect(() => {
    setPropertyAddress(propertyDetailsData?.propertyAddress)
  }, [propertyDetailsData?.propertyAddress])

  useEffect(() => {
    setPropertyAttributes(propertyDetailsData?.propertyAttributes)
  }, [propertyDetailsData?.propertyAttributes])

  const hasUnsavedChanges = useMemo(() => {
    return !(
      isDirtyEqual(propertyAddress, propertyDetailsData?.propertyAddress) &&
      isDirtyEqual(propertyAttributes, propertyDetailsData?.propertyAttributes)
    )
  }, [
    isPending,
    propertyAddress,
    propertyAttributes,
    propertyDetailsData?.propertyAddress,
    propertyDetailsData?.propertyAttributes,
  ])

  useEffect(() => {
    if (hasUnsavedChanges) {
      setHasSaved(false)
      setHasError(false)
    }
  }, [hasUnsavedChanges])

  const saveState = useMemo(() => {
    if (isSaving) return SAVE_BUTTON_STATES.SAVING
    if (hasError) return SAVE_BUTTON_STATES.ERROR
    if (hasSaved) return SAVE_BUTTON_STATES.SAVED
    if (hasUnsavedChanges) return SAVE_BUTTON_STATES.UNSAVED
    return SAVE_BUTTON_STATES.UNSAVED
  }, [hasError, hasSaved, hasUnsavedChanges, isSaving])

  const handleSaveChanges = useCallback(async () => {
    if (!campaignId || !hasUnsavedChanges || isSaving) return
    if (!propertyAddress) return

    setIsSaving(true)
    setHasError(false)
    try {
      await staffUpdatePropertyDetailsForCampaign({
        campaignId,
        body: {
          propertyAddress,
          propertyAttributes,
        },
      })
      await Promise.all([
        invalidateStaffGetPropertyDetails(campaignId),
        invalidateStaffGetPropertyInformationProgress(campaignId),
      ])
      setHasSaved(true)
      updateToastState({
        color: 'success',
        message: 'Property details saved successfully',
        open: true,
      })
    } catch {
      setHasError(true)
      updateToastState({
        color: 'error',
        message: 'Error saving property details',
        open: true,
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    campaignId,
    hasUnsavedChanges,
    isSaving,
    propertyAddress,
    propertyAttributes,
    invalidateStaffGetPropertyDetails,
    invalidateStaffGetPropertyInformationProgress,
    staffUpdatePropertyDetailsForCampaign,
    updateToastState,
  ])

  return {
    propertyAddress,
    setPropertyAddress,
    hasUnsavedChanges,
    saveState,
    isSaving,
    isPending,
    isError,
    propertyAttributes,
    hidePropertyAttributes,
    setPropertyAttributes,
    handleSaveChanges,
  }
}

export default usePropertyDetailsSectionState
